import React from 'react';
import { Badge, Box, Heading } from '@razorpay/blade/components';
import { useFormWithProvider } from 'forms';
import {
  ReviewSection,
  ReviewSectionContainer,
  ReviewSectionHeader,
  ReviewSectionItem,
  ReviewSectionItemContainer,
} from 'components/WizardViews/components/review';
import { FlexibleBenefitFormData } from './types';

interface ReviewStepProps {
  onEditBasicDetails: () => void;
  onEditAmountDetails: () => void;
  onEditTaxDetails: () => void;
}

export const ReviewStep: React.FC<ReviewStepProps> = ({
  onEditBasicDetails,
  onEditAmountDetails,
  onEditTaxDetails,
}) => {
  const { formData } = useFormWithProvider<FlexibleBenefitFormData>();

  const formatBenefitAmount = () => {
    const settings = formData.benefit_amount_settings;
    const benefitAmountType = settings?.type;

    if (benefitAmountType === 'FIXED' || benefitAmountType === 'RANGE') {
      if (settings?.unit === 'ACTUAL_AMOUNT') {
        return `Max Limit - ₹${settings.value || 0}`;
      } else if (settings?.unit === 'PERCENTAGE_OF_BASIC') {
        return `Max Limit - ${settings.value || 0}% of Basic`;
      }
    } else if (benefitAmountType === 'LIST_OF_VALUES') {
      const options = settings?.options || [];
      if (options.length === 0) return 'No values selected';

      return (
        <Box display="flex" gap="spacing.2" flexWrap="wrap">
          {options.map((option: string | number, index: number) => (
            <Badge key={index} color="neutral" emphasis="subtle">
              ₹{option}
            </Badge>
          ))}
        </Box>
      );
    } else if (!Boolean(formData.declaration_settings?.declaration_enabled)) {
      return `₹${settings?.value || 0}`;
    }

    return 'Not set';
  };

  const formatDeclarationMethod = () => {
    if (formData.declaration_settings?.declaration_enabled === 'true') {
      return 'Allow declaration';
    } else {
      return 'Auto-apply full amount';
    }
  };

  const formatBenefitType = () => {
    return formData.nonCashBenefit ? 'Non-cash benefit' : 'Cash benefit';
  };

  const formatUnclaimedAmount = () => {
    if (formData.hold_settings?.hold_unclaimed_amount === 'true') {
      return 'Hold';
    } else if (formData.hold_settings?.hold_unclaimed_amount === 'false') {
      return 'Release';
    }
    return 'Not set';
  };

  return (
    <Box padding="spacing.6" display="flex" flexDirection="column" gap="spacing.6">
      <Box>
        <Heading size="large" weight="semibold" color="surface.text.gray.normal">
          Review
        </Heading>
      </Box>

      <ReviewSectionContainer>
        <ReviewSection>
          <ReviewSectionHeader title="Basic" subtitle="details" onEditClick={onEditBasicDetails} />
          <ReviewSectionItemContainer>
            <ReviewSectionItem
              label="Component Name"
              value={formData.name || 'Not set'}
              status={formData.name ? 'success' : 'error'}
            />
            <ReviewSectionItem
              label="Payslip & Salary Register name"
              value={formData.displayName || 'Not set'}
              status={formData.displayName ? 'success' : 'error'}
            />
            {formData.description && (
              <ReviewSectionItem
                label="Component Description"
                value={formData.description}
                status="success"
              />
            )}
            <ReviewSectionItem
              label="Include in Gross Salary"
              value={formData.showInGrossSalary ? 'Yes' : 'No'}
              status="success"
            />
            <ReviewSectionItem label="Benefit type" value={formatBenefitType()} status="success" />
          </ReviewSectionItemContainer>
        </ReviewSection>

        {/* Amount Details Section */}
        <ReviewSection>
          <ReviewSectionHeader
            title="Amount"
            subtitle="details"
            onEditClick={onEditAmountDetails}
          />
          <ReviewSectionItemContainer>
            <ReviewSectionItem
              label="Declaration Mode"
              value={formatDeclarationMethod()}
              status="success"
            />
            <ReviewSectionItem
              label="Benefit amount"
              value={formatBenefitAmount()}
              status={
                formData.benefit_amount_settings?.value ||
                (formData.benefit_amount_settings?.options?.length ?? 0) > 0
                  ? 'success'
                  : 'error'
              }
            />
          </ReviewSectionItemContainer>
        </ReviewSection>

        <ReviewSection>
          <ReviewSectionHeader
            title="Taxability"
            subtitle="details"
            onEditClick={onEditTaxDetails}
          />
          <ReviewSectionItemContainer>
            <ReviewSectionItem
              label="Tax exempted in Old Regime"
              value={formData.tax_behaviour?.tax_exempt_in_old_regime ? 'Yes' : 'No'}
              status="success"
            />
            <ReviewSectionItem
              label="Tax exempted in New Regime"
              value={formData.tax_behaviour?.tax_exempt_in_new_regime ? 'Yes' : 'No'}
              status="success"
            />
            <ReviewSectionItem
              label="Proration"
              value={
                formData.tax_behaviour?.tax_deduction_behaviour === 'pro_rated'
                  ? 'Enabled'
                  : 'Disabled'
              }
              status="success"
            />
            <ReviewSectionItem
              label="Arrear calculation"
              value={formData.arrear_behaviour?.consider_in_arrear ? 'Enabled' : 'Disabled'}
              status="success"
            />
            <ReviewSectionItem
              label="Unclaimed amount"
              value={formatUnclaimedAmount()}
              status={formData.hold_settings?.hold_unclaimed_amount ? 'success' : 'error'}
            />
          </ReviewSectionItemContainer>
        </ReviewSection>
      </ReviewSectionContainer>
    </Box>
  );
};
